import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom, catchError, of, throwError } from 'rxjs';
import { tap, shareReplay, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { PhotosApiService } from '@core/api/services/photos-api.service';
import { UserFullInfoDto } from '@shared/models/user-full-info.model';

@Injectable({
  providedIn: 'root'
})
export class UserFullInfoService {
  private readonly apiUrl = `${environment.apiUrl}/me`;
  private userSubject = new BehaviorSubject<UserFullInfoDto | null>(null);
  private errorSubject = new BehaviorSubject<string | null>(null);
  private userObservable$: Observable<UserFullInfoDto | null>;

  readonly hasActivity$ = this.userSubject.pipe(
    map(user => this.hasActivitySnapshot(user))
  );

  readonly canManage$ = this.userSubject.pipe(
    map(user => !!(user?.isAdmin || user?.canManage))
  );

  readonly canManageMemberData$ = this.userSubject.pipe(
    map(user => this.canManageMemberDataSnapshot(user))
  );

  readonly canManageDivineServices$ = this.userSubject.pipe(
    map(user => this.canManageDivineServicesSnapshot(user))
  );

  readonly canViewRestrictedData$ = this.userSubject.pipe(
    map(user => !!(user?.isAdmin || user?.canViewRestrictedData))
  );

  private loadingPromise: Promise<UserFullInfoDto | null> | null = null;
  private subscribed = false;

  constructor(private http: HttpClient, private photos: PhotosApiService) {
    this.userObservable$ = this.createUserObservable();
    this.photos.changed.subscribe(id => {
      if (id === this.userSubject.value?.personId) void this.refreshUserInfo().catch(() => undefined);
    });
  }

  hasActivitySnapshot(user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return !!(
      user?.isAdmin ||
      user?.canManage ||
      user?.canViewRestrictedData ||
      user?.activeMinistry ||
      user?.activeAppointments?.length ||
      user?.activeAssignments?.length ||
      user?.activeActivities?.length
    );
  }

  canManageSnapshot(user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return !!(user?.isAdmin || user?.canManage);
  }

  canManageMemberDataSnapshot(user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return this.hasPermission('ManagePeopleGeneral', user);
  }

  canManageDivineServicesSnapshot(user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return this.hasPermission('ManageDivineServices', user);
  }

  canViewRestrictedDataSnapshot(user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return this.hasPermission('ViewUnitDetail', user);
  }

  hasPermission(permission: string, user: UserFullInfoDto | null = this.userSubject.value): boolean {
    return !!(user?.isAdmin || user?.permissions?.some(item => item.toLowerCase() === permission.toLowerCase()));
  }

  getUserInfo$(): Observable<UserFullInfoDto | null> {
    if (!this.subscribed) {
      this.subscribed = true;
      this.userObservable$.subscribe({
        error: () => {
          this.subscribed = false;
        }
      });
    }

    return this.userSubject.asObservable();
  }

  getError$(): Observable<string | null> {
    return this.errorSubject.asObservable();
  }

  getUserInfoSnapshot(): UserFullInfoDto | null {
    return this.userSubject.value;
  }

  getErrorSnapshot(): string | null {
    return this.errorSubject.value;
  }

  loadUserInfo(): Promise<UserFullInfoDto | null> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    if (this.userSubject.value !== null || this.errorSubject.value !== null) {
      return Promise.resolve(this.userSubject.value);
    }

    this.getUserInfo$();

    this.loadingPromise = firstValueFrom(this.userObservable$)
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  refreshUserInfo(): Promise<UserFullInfoDto | null> {
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.subscribed = true;
    this.errorSubject.next(null);
    this.userObservable$ = this.createUserObservable();
    this.loadingPromise = firstValueFrom(this.userObservable$)
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  clear(): void {
    this.photos.clear();
    this.userSubject.next(null);
    this.errorSubject.next(null);
    this.loadingPromise = null;
    this.subscribed = false;
    this.userObservable$ = this.createUserObservable();
  }

  private createUserObservable(): Observable<UserFullInfoDto | null> {
    return this.http.get<UserFullInfoDto>(this.apiUrl).pipe(
      tap(user => {
        this.userSubject.next(user);
        this.errorSubject.next(null);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.userSubject.next(null);
          this.errorSubject.next(null);
          return throwError(() => error);
        }

        const errorMessage = error?.error?.detail || 'No se pudo cargar la información del usuario. Volvé a intentarlo.';
        console.error('Error en UserFullInfoService:', errorMessage, error);
        this.errorSubject.next(errorMessage);
        return of(null);
      }),
      shareReplay(1)
    );
  }
}
